import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import { useUpsertReviewMutation } from '../../apis/mlkApi/mlk.api.hooks';
import { Button } from '../../components/Button/Button';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import { Stars } from '../../components/Stars/Stars';
import { Config } from '../../helpers/config';
import { useLanguage } from '../../locale/useLanguage';
import { Vote } from '../../models';
import { Routes } from '../../routes/types';
import './ReviewView.css';

type ReviewViewProps = {};

export const ReviewView: React.FC<ReviewViewProps> = () => {
  const [t] = useLanguage();
  const history = useHistory();

  const [starVote, setStarVote] = useState<Vote | undefined>();
  const [comment, setComment] = useState<string | undefined>();
  const [orderResponse, setOrderResponse] = useState<OrderRequestResult>();

  const [upsertReview] = useUpsertReviewMutation();

  const confirm = useCallback(async () => {
    try {
      await upsertReview({
        opaqueOrderId: orderResponse!.data!.opaqueId,
        rating: starVote!,
        comment,
      }).unwrap();
      history.push(Routes.Home);
    } catch (e) {
      // TODO: handle error
    }
  }, [comment, history, orderResponse, starVote, upsertReview]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    if (!orderResponse?.isFetching) {
      setStarVote(orderResponse?.data?.review?.rating);
      setComment(orderResponse?.data?.review?.comment);
    }
  }, [
    orderResponse?.data?.review?.comment,
    orderResponse?.data?.review?.rating,
    orderResponse?.isFetching,
  ]);

  const navigateBack = useCallback(() => {
    history.push(Routes.Home);
  }, [history]);

  const updateReview = useCallback((orderResult: OrderRequestResult) => {
    setOrderResponse(orderResult);
  }, []);

  const onCommentChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    const newCommentText = e.target.value;
    if (newCommentText.length <= 250) {
      setComment(e.target.value);
    }
  }, []);

  const feedbackText = useMemo(() => {
    switch (starVote) {
      case 1:
        return 'Pessima';
      case 2:
        return 'Insufficiente';
      case 3:
        return 'Sufficiente';
      case 4:
        return 'Buona';
      case 5:
        return 'Ottima!';
      default:
        return '';
    }
  }, [starVote]);

  const commentLength = comment?.length ?? 0;

  return (
    <Layout
      title={t('pageTitles.reviewDelivery')}
      hasBack
      onBackClick={navigateBack}
      onOrderChange={updateReview}
    >
      <section className="review-view--star-section-container">
        <h2 className="title">{t('reviewPage.starSectionTitle')}</h2>
        <div className="review-view--star-line-container">
          <Stars
            filledStarsNumber={starVote}
            onStarClick={setStarVote}
            color={Config.primaryBlue}
            width="24"
            height="24"
            containerClass="review-view--star-container"
          />
          <p className="review-view--review-value">{feedbackText}</p>
        </div>
        <p className="paragraph review-view--star-section-description">
          {t('reviewPage.starSectionDescription')}
        </p>
      </section>
      <section>
        <label>
          <h2 className="title">{t('reviewPage.commentSectionTitle')}</h2>
          <textarea
            value={comment}
            onChange={onCommentChange}
            placeholder={t('reviewPage.commentInputPlaceholder')}
            rows={6}
            className="textarea"
            name="comment"
          />
        </label>
        <p className="review-view--word-counter">{commentLength}/250</p>
      </section>
      <div className="review-view--actions-container">
        <div className="review-view--button">
          <Button
            isDisabled={Boolean(!starVote)}
            isPrimary
            label={t('reviewPage.confirmButton')}
            onClick={confirm}
          />
        </div>
        <div>
          <Button onClick={navigateBack} label={t('reviewPage.cancelButton')} />
        </div>
      </div>
    </Layout>
  );
};
